'use client';

import { useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useLanguage } from '@/components/LanguageProvider';
import { ChevronLeft, Check, Copy } from 'lucide-react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function SingleNewsPage() {
  const params = useParams();
  const { locale } = useLanguage();
  const router = useRouter();
  
  const [article, setArticle] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  const slug = params.id;

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await fetch(`https://sg-public-api.royolands.com/rpweb/api/news/${slug}?lang=${locale}`);
        if (res.ok) {
          const data = await res.json();
          setArticle(data);
        } else {
          router.push('/news'); 
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) fetchArticle();
  }, [slug, locale, router]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  if (loading) {
    return (
      <main className="bg-[#0a0a0a] min-h-screen pt-[115px] sm:pt-[88px] flex flex-col">
        <Header />
        <div className="flex-1 max-w-[1368px] mx-auto px-4 lg:px-9 py-20 flex flex-col gap-6 w-full">
           <div className="h-16 w-3/4 bg-white/[0.03] rounded-2xl animate-pulse" />
           <div className="h-64 w-full bg-white/[0.03] rounded-[32px] animate-pulse mt-8" />
        </div>
      </main>
    );
  }

  if (!article) return null;

  return (
    // Легкая структура flex-col для правильной работы Lenis
    <div className="bg-[#050505] flex flex-col min-h-screen">
      
      {/* Главный блок контента с закруглением */}
      <main className="flex-1 relative bg-[#0a0a0a] text-white pt-[115px] sm:pt-[88px] selection:bg-[#667fff] selection:text-white pb-20 rounded-b-[40px] md:rounded-b-[48px] z-10 shadow-2xl overflow-hidden">
        <Header />
        
        <article className="relative w-full max-w-[1368px] mx-auto px-4 md:px-8 xl:px-9 py-8 md:py-16">
          
          <div className="pb-8 md:pb-10">
            <Link 
              href="/news" 
              className="inline-flex items-center gap-1.5 text-[17px] leading-[24px] text-white hover:opacity-50 transition-opacity"
            >
              <ChevronLeft size={20} />
              {locale === 'ru' ? 'Новости' : 'News'}
            </Link>
          </div>

          <h1 className="m-0 text-4xl md:text-[44px] lg:text-[56px] leading-[1.05] font-medium tracking-tight text-white mb-10 md:mb-16">
            {article.title}
          </h1>

          {article.imageUrl && (
            <div className="w-full aspect-[16/9] md:aspect-[21/9] rounded-[24px] overflow-hidden mb-12 bg-[#111]">
              <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="relative">
            <div className="pb-6">
              <span className="text-[15px] leading-[20px] text-[#888888] font-medium uppercase tracking-wide">
                {new Date(article.createdAt).toLocaleDateString(locale === 'ru' ? 'ru-RU' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>

            <div className="absolute left-0 right-0 top-[44px] border-t border-white/10" />

            <div className="mt-12 lg:ml-[42%] text-[#d1d1d1] text-[17px] md:text-[21px] leading-[24px] md:leading-[28px] mb-[80px] md:mb-[120px]">
              <div className="prose prose-invert max-w-none 
                prose-p:mt-0 prose-p:mb-[28px] prose-p:break-words
                prose-headings:text-white prose-headings:font-medium prose-headings:tracking-tight
                prose-a:text-[#667fff] prose-a:no-underline hover:prose-a:underline
                prose-img:rounded-[16px] prose-hr:border-white/10"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            {/* Теги */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-col md:flex-row w-full pb-10 md:pb-14 border-b border-white/5 mb-10">
                <div className="w-full md:w-[42%] flex-shrink-0 mb-6 md:mb-0">
                  <h4 className="text-[23px] md:text-[29px] leading-tight font-medium m-0">
                    {locale === 'ru' ? 'Теги' : 'Tags'}
                  </h4>
                </div>
                <div className="w-full md:w-[58%] flex flex-wrap gap-3">
                  {article.tags.map((tag: string) => (
                    <Link 
                      key={tag}
                      href={`/news?tag=${tag}`}
                      className="px-4 py-2 bg-white/[0.03] border border-white/10 rounded-xl text-[17px] text-[#667fff] hover:bg-[#667fff]/10 transition-colors font-medium"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Автор */}
            <div className="flex flex-col md:flex-row w-full pb-10 md:pb-14 border-b border-white/5 mb-10">
              <div className="w-full md:w-[42%] flex-shrink-0 mb-6 md:mb-0">
                <h4 className="text-[23px] md:text-[29px] leading-tight font-medium m-0">
                  {locale === 'ru' ? 'Автор' : 'Author'}
                </h4>
              </div>
              <div className="w-full md:w-[58%]">
                <Link 
                  href={`/user?nickname=${article.author}`}
                  className="inline-flex items-center px-5 py-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-[17px] text-white hover:bg-[#667fff]/10 hover:border-[#667fff]/50 hover:text-[#667fff] transition-all duration-300 font-medium"
                >
                  @{article.author}
                </Link>
              </div>
            </div>

            {/* Поделиться */}
            <div className="flex flex-col md:flex-row w-full pt-0">
              <div className="w-full md:w-[42%] flex-shrink-0 mb-6 md:mb-0">
                <h4 className="text-[23px] md:text-[29px] leading-tight font-medium m-0">
                  {locale === 'ru' ? 'Поделиться' : 'Share'}
                </h4>
              </div>
              <div className="w-full md:w-[58%] flex items-center gap-3">
                <button 
                  onClick={copyLink}
                  className={`relative px-6 py-3 font-medium rounded-xl transition-all duration-300 flex items-center gap-2 justify-center min-w-[220px] ${
                    isCopied 
                      ? 'bg-green-500/10 text-green-400 border border-green-500/30' 
                      : 'bg-white text-[#050505] hover:opacity-80'
                  }`}
                >
                  {isCopied ? (
                    <>
                      <Check size={18} />
                      {locale === 'ru' ? 'Ссылка скопирована' : 'Link copied!'}
                    </>
                  ) : (
                    <>
                      <Copy size={18} />
                      {locale === 'ru' ? 'Копировать ссылку' : 'Copy link'}
                    </>
                  )}
                </button>
              </div>
            </div>

          </div>
        </article>
      </main>

      {/* Футер в нормальном потоке (Lenis счастлив) */}
      <div className="bg-[#050505] pt-12 relative z-0">
        <Footer />
      </div>
    </div>
  );
}